import { Component } from 'react'
import Head from 'next/head'

import AppBar from 'Components/AppBar'
import Button from 'Components/Button'
import ExternalLink from 'Components/ExternalLink'
import Footer from 'Components/Footer'
import Form from 'Components/Form'
import Input from 'Components/Input'

import {
  SVGIconLink,
  SVGTelephoneTiny,
  SVGTickSuccess,
  SVGSupport
} from 'Components/SVGCollection'

import cn from 'classnames'
import cg from 'Scss/app.scss'
import cl from 'Scss/pages/password.scss'
import btn from 'Components/Button/Button.scss'
import PageLink from 'Components/PageLink'
import { AppContext } from 'Utils/withReduxStore'
import { pushRoute, redirect } from 'Utils/helpers/routerHelper'
import _app from './_app'
import passwordResetMutation from 'Utils/api/gql/mutations/passwordReset'
import { passwordReset, passwordResetVariables, setPassword, setPasswordVariables, CartFragment } from 'Utils/api/gql/types'
import setPasswordMutation from 'Utils/api/gql/mutations/setPassword'
import { connect, DispatchProp } from 'react-redux'
import { tokenLogin, redirectSafe } from 'Utils/redux/actions'
import { showMessage, showErrorModal } from 'Utils/helpers/modalHelper'
import GoodIdButton from 'Components/GoodIdButton';
import { collectAllErrorsForModal } from 'Utils/helpers/errorHelper';

type PropType = {
  cart: CartFragment | null
  subpage: string | null
  email: string | null
  rpToken: string | null
} & DispatchProp

type StateType = {
  email: string
  password1: string
  password2: string
  error?: string
}

class PasswordPage extends Component<PropType, StateType> {
  state: StateType = {
    email: '',
    password1: '',
    password2: ''
  }

  static getInitialProps = async ({ query, initialState, res }: AppContext): Promise<Partial<PropType>> => {
    if (initialState.session.loggedIn) {
      redirect('/profil', res)
      return { }
    }
    const subpage = (query.subpage as string) || null
    const email = (query.email as string) || null
    const rpToken = (query.rpToken as string) || null

    return { subpage, email, rpToken }
  }

  handleSendClick = async () => {
    const { email } = this.state
    try {
      const result = await _app.apolloClient.mutate<passwordReset, passwordResetVariables>({
        mutation: passwordResetMutation,
        variables: { email }
      })

      if (result.data && result.data.passwordReset) {
        pushRoute('/elfelejtett-jelszo/elkuldve')
      } else {
        throw new Error()
      }
    } catch (e) {
      showErrorModal('A megadott e-mail címmel nincs regisztrált felhasználónk! Segítségért hívja ügyfélszolgálatunkat.')
    }
  }

  handleResetClick = async () => {
    const { email, rpToken } = this.props
    const { password1, password2 } = this.state

    if (password1 !== password2) {
      this.setState({ error: 'A megadott jelszavak nem egyeznek!' })
      return
    } else {
      this.setState({ error: undefined })
    }

    try {
      const result = await _app.apolloClient.mutate<setPassword, setPasswordVariables>({
        mutation: setPasswordMutation,
        variables: { input: { email: email || '', token: rpToken || '', password: password1, cart_id: this.props.cart && this.props.cart.id || undefined }}
      })

      if (result.data && result.data.setPassword.user && result.data.setPassword.token) {
        this.props.dispatch(tokenLogin(result.data.setPassword.token, result.data.setPassword.user))
        this.props.dispatch(redirectSafe('/profil'))
        showMessage('Sikeres jelszóváltoztatás!')
      }
    } catch (e) {
      showErrorModal(collectAllErrorsForModal(e))
    }
  }

  renderPasswordReminder() {
    return (
      <div className={cn(cg.gridContainer, cl.password__container)}>
        <h1 className={cn(cg.h2, cl.h2)}>Új jelszó igénylés</h1>

        <p className={cl.lead}>
          Add meg a regisztrált e-mail címed, melyre elküldjük a bejelentkezéshez szükséges adatokat.
        </p>

        <div className={cl.passwordReminder__formContainer}>
          <div className={cg.gridX}>
            <div className={cn(cg.cell, cg.mediumAuto)}>
              <Input
                name="email" type="email" autoComplete="email"
                value={this.state.email}
                onChange={({ currentTarget: { value } }) => { this.setState({ email: value }) }}
                placeholder="E-mail cím"
                label="E-mail cím"
                hideLabel
              />
            </div>

            <div className={cn(cg.cell, cg.mediumShrink, cg.textRight, cl.passwordReminder__buttonContainer)}>
              <Button className={cn(btn.secondary)} label="Elküld" onClick={this.handleSendClick} />
            </div>
          </div>
          <div>
            <GoodIdButton />
          </div>
        </div>

        <h2 className={cn(cg.h4, cl.h4)}>Még nem regisztráltál?</h2>

        <PageLink toPage="/regisztracio">
          <a className={cn(cg.linkWithIcon, cl.linkWithIcon)}>
            Regisztráció

            <span className={cg.linkWithIcon__iconContainer}>
              <SVGIconLink width={15} height={15} />
            </span>
          </a>
        </PageLink>

        <div className={cl.passwordReminder__serviceBox}>
          <div className={cn(cg.gridX, cg.alignMiddle)}>
            <div className={cn(cg.cell, cg.mediumAuto)}>
              <h2 className={cn(cg.h4, cl.h4)}>Regisztráltál, de nem emlékszel az e-mail címedre?</h2>

              <p className={cl.lead}>
                Keresd kollégáinkat, akik készséggel állnak rendelkezésedre.
              </p>
            </div>

            <div className={cn(cg.cell, cg.mediumShrink)}>
              <div className={cl.passwordReminder__linkContainer}>
                <ExternalLink href="mailto:e-shop@groby.hu">
                  <span className={cl.email}>
                    e-shop@groby.hu
                  </span>
                </ExternalLink>
              </div>

              <div className={cl.passwordReminder__linkContainer}>
                <ExternalLink href="tel:+3614763688">
                  <span className={cn(cg.gridX, cg.alignMiddle, cl.telephone)}>
                    <span className={cn(cg.cell, cg.shrink)} aria-hidden={true}>
                      <SVGTelephoneTiny width={12} height={12} />
                    </span>

                    <span className={cn(cg.cell, cg.auto)}>
                      (06 1) 476 36 88
                    </span>
                  </span>
                </ExternalLink>
              </div>

              <div className={cl.iconWrapper} aria-hidden={true}>
                <SVGSupport width="36" height="36" />
              </div>

            </div>
          </div>
        </div>
      </div>
    )
  }

  renderPasswordReminderSuccess() {
    return (
      <div className={cn(cg.gridContainer, cl.password__container, cl.password__containerIsSuccess)}>
        <div className={cn(cl.passwordReminder__illustrationContainer, cg.alignCenter, cg.textCenter)} aria-hidden={true}>
          <SVGTickSuccess width={71} height={71} />
        </div>

        <h1 className={cn(cg.h2, cl.h2)}>Jelszó emlékeztető</h1>

        <p className={cl.lead}>
          A belépéshez szükséges adatokat elküldtük a(z) <strong>{this.state.email}</strong> e-mail címre.
          <br />
          Kattints az üzenetben található linkre!
        </p>
      </div>
    )
  }

  renderNewPassword() {
    return (
      <div className={cn(cg.gridContainer, cl.password__container, cl.newPassword__container)}>
        <h1 className={cn(cg.h2, cl.h2)}>Új jelszó beállítása</h1>

        <p className={cl.lead}>
          Add meg új jelszavad, amit a továbbiakban használni szeretnél!
        </p>

        <div>
          <Form>
            <Input value={this.state.password1} onChange={({currentTarget: { value }}) => this.setState({ password1: value })} name="newPassword" label="Új jelszó" type="password" autoComplete="new-password"  />
            <Input error={this.state.error} value={this.state.password2} onChange={({currentTarget: { value }}) => this.setState({ password2: value })} name="newPassword2" label="Új jelszó újra" type="password" autoComplete="new-password"  />
            <div className={cn(cg.textRight, cl.newPassword__buttonContainer)}>
              <Button label="Beállít" onClick={this.handleResetClick} />
            </div>
          </Form>
        </div>
      </div>
    )
  }

  render() {
    const { subpage, rpToken } = this.props

    let content = this.renderPasswordReminder()

    if (subpage === 'elkuldve' && this.state.email) {
      content = this.renderPasswordReminderSuccess()
    }

    if (rpToken) {
      content = this.renderNewPassword()
    }

    return (
      <div>
        <Head>
          <title>Elfelejtett jelszó</title>
        </Head>

        <AppBar
          pageType="Home"
          breadcrumb="Home / Password Reminder"
        />

        {content}

        <Footer />
      </div>
    )
  }
}

export default connect(({ cart }: Redux.IReduxState) => ({
  cart
}))(PasswordPage)
